import React, { useState, useRef } from "react";
import {
  Button, TextField, CircularProgress, InputAdornment,
  IconButton, FormControlLabel, Checkbox, Link, Box, Typography, Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { loginUser, getRedirectUrl } from "../../services/AuthentificationService";
import useToken from "../../utils/useToken";
import { Redirect, useLocation } from "react-router-dom";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Root = styled(Box)({
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  width: '100vw',
  overflow: 'hidden',
  fontFamily: "'Fira Sans', sans-serif",
  background: 'linear-gradient(135deg, #02357a 0%, #03407b 42%, #1474b7 72%, #0ea5e9 100%)',
  perspective: 1400,
});

const BrandPanel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
  overflow: 'hidden',
  zIndex: 0,
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(5, 3),
  },
}));

const Circle = styled(Box)(({ size, top, left, opacity = 0.12 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  top,
  left,
  borderRadius: '50%',
  background: 'rgba(255,255,255,' + opacity + ')',
  pointerEvents: 'none',
}));

const Ring = styled(Box)(({ size, top, left, borderWidth = 2 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  top,
  left,
  borderRadius: '50%',
  border: `${borderWidth}px solid rgba(255,255,255,0.2)`,
  pointerEvents: 'none',
}));

const FormPanel = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  width: '100vw',
  padding: theme.spacing(5),
  margin: '0 auto',
  background: 'radial-gradient(circle at 50% 44%, rgba(255,255,255,0.12), transparent 30%)',
  [theme.breakpoints.down('md')]: {
    width: '100vw',
    alignItems: 'center',
    padding: theme.spacing(4, 3),
    background: 'radial-gradient(circle at 50% 35%, rgba(255,255,255,0.14), transparent 38%)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3),
  },
}));

const FormBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  maxWidth: 570,
  padding: theme.spacing(5.5, 6),
  borderRadius: 34,
  background: 'linear-gradient(145deg, rgba(255,255,255,0.24), rgba(255,255,255,0.09))',
  border: '1px solid rgba(255, 255, 255, 0.42)',
  boxShadow: '0 38px 110px rgba(2, 29, 71, 0.46), inset 0 1px 0 rgba(255,255,255,0.62), inset 0 -1px 0 rgba(255,255,255,0.14)',
  backdropFilter: 'blur(30px) saturate(165%)',
  transformStyle: 'preserve-3d',
  animation: 'loginCardFloat 7s ease-in-out infinite',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-45% auto -45% -90%',
    width: '72%',
    background: 'linear-gradient(110deg, transparent 8%, rgba(255,255,255,0.18) 35%, rgba(255,255,255,0.64) 48%, rgba(255,255,255,0.18) 62%, transparent 86%)',
    transform: 'rotate(12deg)',
    animation: 'loginGlassSweep 5.8s ease-in-out infinite',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 14,
    borderRadius: 26,
    border: '1px solid rgba(255,255,255,0.24)',
    boxShadow: 'inset 0 0 38px rgba(255,255,255,0.08)',
    pointerEvents: 'none',
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
  '@keyframes loginCardFloat': {
    '0%, 100%': { transform: 'translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg)' },
    '50%': { transform: 'translate3d(0, -10px, 26px) rotateX(1.4deg) rotateY(-1.8deg)' },
  },
  '@keyframes loginGlassSweep': {
    '0%, 38%': { left: '-92%', opacity: 0 },
    '48%': { opacity: 1 },
    '72%, 100%': { left: '126%', opacity: 0 },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: 20,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.88)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.96)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.8)' },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: '0 0 0 4px rgba(255,255,255,0.16), 0 12px 26px rgba(2,29,71,0.18)',
      '& fieldset': { borderColor: '#03407b', borderWidth: 2 },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#03407b',
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.6),
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  background: 'linear-gradient(135deg, #03407b 0%, #02357a 100%)',
  boxShadow: '0 4px 15px rgba(99,102,241,0.4)',
  transition: 'all 0.25s ease',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #02357a 0%, #022d68 100%)',
    boxShadow: '0 6px 20px rgba(99,102,241,0.5)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
  '&.Mui-disabled': {
    background: '#e2e8f0',
    boxShadow: 'none',
    color: '#94a3b8',
  },
}));

const circleMotion = (x = 18, y = 16, duration = 9) => ({
  x: [0, x, -x * 0.35, 0],
  y: [0, -y, y * 0.45, 0],
  scale: [1, 1.04, 0.98, 1],
  transition: {
    duration,
    repeat: Infinity,
    repeatType: "mirror",
    ease: "easeInOut",
  },
});

const panelVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08 + 0.2, duration: 0.45, ease: 'easeOut' },
  }),
};

const leftVariants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function Login({ snackbarShowMessage }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ username: '', password: '' });
  const [apiError, setApiError] = useState('');
  const mounted = useRef(true);
  const { setToken } = useToken();
  const [redirectToReferrer, setRedirectToReferrer] = useState(
    localStorage.getItem("token") ? true : false
  );
  const { state } = useLocation();

  const validate = () => {
    const temp = {
      username: username ? "" : "L'adresse email est obligatoire.",
      password: password ? "" : "Le mot de passe est obligatoire.",
    };
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSubmit(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await loginUser({ username, password });
      if (mounted.current) {
        setToken(response.data);
        localStorage.setItem("refresh_token", response.data.refresh_token);
        const userData = response.data.data;
        localStorage.setItem("userData", JSON.stringify(userData));
        localStorage.setItem("rolecurrentuser", userData.role[0]);

        const columnsVisibility = userData.columnsVisibility;
        if (columnsVisibility && typeof columnsVisibility === 'object') {
          Object.entries(columnsVisibility).forEach(([key, value]) => {
            localStorage.setItem(key, value);
          });
        }
        localStorage.setItem("rowsPerPage", JSON.stringify(userData.rowsPerPage ?? {}));
        snackbarShowMessage("Connexion réussie", "success", 4000);
        window.location.reload();
        setRedirectToReferrer(true);
      }
    } catch (err) {
      if (mounted.current) {
        const msg = err.response?.data?.message || "Identifiants incorrects. Veuillez réessayer.";
        setApiError(msg);
        snackbarShowMessage(msg, "error", 4000);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  if (redirectToReferrer) {
    return <Redirect to={getRedirectUrl()} />;
  }

  return (
    <Root>
      
      <BrandPanel
        component={motion.div}
        variants={leftVariants}
        initial="hidden"
        animate="visible"
      >
        
        <Circle component={motion.div} animate={circleMotion(28, 20, 11)} size={480} top="-160px" left="-120px" opacity={0.11} />
        <Circle component={motion.div} animate={circleMotion(-22, 26, 10)} size={280} top="61%" left="58%" opacity={0.09} />
        <Circle component={motion.div} animate={circleMotion(16, -18, 8)} size={130} top="14%" left="72%" opacity={0.16} />
        <Ring component={motion.div} animate={{ rotate: [0, 360], scale: [1, 1.03, 1], transition: { duration: 22, repeat: Infinity, ease: "linear" } }} size={320} top="-72px" left="45%" />
        <Ring component={motion.div} animate={circleMotion(18, 12, 12)} size={190} top="70%" left="-42px" />
        <Ring component={motion.div} animate={circleMotion(-12, 10, 7)} size={86} top="42%" left="79%" borderWidth={1.5} />

        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 120,
          background: 'linear-gradient(to top, rgba(2, 53, 122, 0.38), transparent)',
          pointerEvents: 'none',
        }} />
      </BrandPanel>

      
      <FormPanel
        component={motion.div}
        variants={panelVariants}
        initial="hidden"
        animate="visible"
      >
        <FormBox>
          
          <Box sx={{ textAlign: 'center', mb: 4.5 }}>
            <img
              src={process.env.REACT_APP_LOGO_URL}
              alt="Logo"
              style={{ width: 174, filter: 'brightness(0) invert(1)', marginBottom: 22 }}
            />
            <Typography
              variant="h3"
              sx={{
                color: '#ffffff',
                fontWeight: 900,
                lineHeight: 1.1,
                fontSize: { xs: '1.55rem', md: '2rem' },
                textShadow: '0 14px 30px rgba(2, 29, 71, 0.35)',
                mb: 1.2,
              }}
            >
              Bienvenue sur votre CRM
            </Typography>
            <Typography
              sx={{
                color: '#dff4ff',
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.5,
                maxWidth: 400,
                mx: 'auto',
                textShadow: '0 8px 18px rgba(2, 29, 71, 0.22)',
              }}
            >
              La plateforme tout-en-un pour piloter vos recrutements avec efficacité.
            </Typography>
          </Box>

          
          <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible">
            <Typography variant="h4" sx={{ fontWeight: 850, color: '#ffffff', mb: 0.75, fontSize: '1.45rem', textAlign: 'center', textShadow: '0 8px 18px rgba(2, 29, 71, 0.22)' }}>
              Connexion
            </Typography>
            <Typography sx={{ color: '#cdeeff', fontSize: '1rem', mb: 4, textAlign: 'center', fontWeight: 500 }}>
              Accédez à votre espace de travail
            </Typography>
          </motion.div>

          
          <AnimatePresence>
            {apiError && (
              <motion.div
                initial={{ opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -8, height: 0 }}
                transition={{ duration: 0.25 }}
              >
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 3, fontSize: '1rem' }}
                  onClose={() => setApiError('')}
                >
                  {apiError}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          
          <Box component="form" noValidate onSubmit={handleSubmit}>
            
            <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible">
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f4fbff', mb: 0.75, letterSpacing: '0.01em', textShadow: '0 6px 14px rgba(2, 29, 71, 0.25)' }}>
                  ADRESSE EMAIL
                </Typography>
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  placeholder="vous@exemple.com"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (errors.username) setErrors((p) => ({ ...p, username: '' }));
                  }}
                  error={!!errors.username}
                  helperText={errors.username}
                  size="medium"
                />
              </Box>
            </motion.div>

            
            <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible">
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, fontWeight: 800, color: '#f4fbff', letterSpacing: '0.01em', whiteSpace: 'nowrap', textShadow: '0 6px 14px rgba(2, 29, 71, 0.25)' }}>
                    MOT DE PASSE
                  </Typography>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      color: '#e3f7ff',
                      fontWeight: 750,
                      textDecoration: 'none',
                      textShadow: '0 6px 14px rgba(2, 29, 71, 0.25)',
                      '&:hover': { color: '#ffffff', textDecoration: 'underline' },
                    }}
                  >
                    Mot de passe oublié ?
                  </Link>
                </Box>
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  placeholder="••••••••"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((p) => ({ ...p, password: '' }));
                  }}
                  onKeyDown={handleKeyPress}
                  error={!!errors.password}
                  helperText={errors.password} slotProps={{ input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: '#94a3b8', '&:hover': { color: '#03407b' } }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  } }}
                />
              </Box>
            </motion.div>

            
            <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{
                      color: '#cbd5e1',
                      '&.Mui-checked': { color: '#03407b' },
                      borderRadius: 1,
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '1rem', color: '#d9f2ff', fontWeight: 600 }}>
                    Se souvenir de moi
                  </Typography>
                }
                sx={{ mt: 0.5, mb: 0.5 }}
              />
            </motion.div>

            
            <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible">
              <LoginButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 2.5 }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                    <span>Connexion en cours…</span>
                  </Box>
                ) : (
                  "Se connecter"
                )}
              </LoginButton>
            </motion.div>
          </Box>

          
          <motion.div custom={5} variants={itemVariants} initial="hidden" animate="visible">
            <Typography
              sx={{ mt: 5, textAlign: 'center', fontSize: '1rem', color: '#d9f2ff', fontWeight: 600 }}
            >
              © {new Date().getFullYear()} Vigor CRM · Tous droits réservés
            </Typography>
          </motion.div>
        </FormBox>
      </FormPanel>
    </Root>
  );
}
